import React, { useId } from 'react';
import type { RewardTier } from '../../../shared/types';
import { WarningIcon, GiftIcon } from '../icons/UiIcons';

interface StampsConfigState {
    targetCount: number;       // [5, 20]
    rewardLabel: string;       // récompense finale
    rewardTiers: RewardTier[]; // paliers intermédiaires (1..targetCount-1)
}

interface StampsConfigProps {
    value: StampsConfigState;
    onChange: (next: StampsConfigState) => void;
    errors?: string[];
    disabled?: boolean;
}

const MIN_TARGET = 5;
const MAX_TARGET = 20;

const StampsConfig: React.FC<StampsConfigProps> = ({ value, onChange, errors, disabled }) => {
    const uid = useId();
    const setTarget = (n: number) => {
        const clamped = Math.max(MIN_TARGET, Math.min(MAX_TARGET, n));
        // Filtrer les paliers > nouveau target
        const validTiers = value.rewardTiers.filter(t => t.threshold < clamped);
        onChange({ ...value, targetCount: clamped, rewardTiers: validTiers });
    };

    const setRewardLabel = (label: string) => onChange({ ...value, rewardLabel: label });

    const toggleStampReward = (stampNumber: number) => {
        const existing = value.rewardTiers.find(t => t.threshold === stampNumber);
        if (existing) {
            onChange({
                ...value,
                rewardTiers: value.rewardTiers.filter(t => t.threshold !== stampNumber),
            });
        } else {
            onChange({
                ...value,
                rewardTiers: [...value.rewardTiers, { label: '', threshold: stampNumber }]
                    .sort((a, b) => a.threshold - b.threshold),
            });
        }
    };

    const updateTierLabel = (stampNumber: number, label: string) => {
        onChange({
            ...value,
            rewardTiers: value.rewardTiers.map(t => t.threshold === stampNumber ? { ...t, label } : t),
        });
    };

    return (
        <div className="space-y-6">
            {/* Objectif */}
            <div>
                <label htmlFor={`${uid}-target`} className="block text-sm font-bold text-gray-700 mb-2">
                    Objectif <span className="text-gray-500 font-normal">— nombre de tampons à collecter</span>
                </label>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => setTarget(value.targetCount - 1)}
                        disabled={disabled || value.targetCount <= MIN_TARGET}
                        className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-40 text-lg font-bold"
                        aria-label="Diminuer l'objectif"
                    >
                        −
                    </button>
                    <input
                        id={`${uid}-target`}
                        type="number"
                        min={MIN_TARGET}
                        max={MAX_TARGET}
                        value={value.targetCount}
                        onChange={e => setTarget(parseInt(e.target.value) || MIN_TARGET)}
                        disabled={disabled}
                        className="w-20 p-2.5 text-center rounded-xl border border-gray-400 bg-white outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 font-bold text-lg"
                    />
                    <button
                        type="button"
                        onClick={() => setTarget(value.targetCount + 1)}
                        disabled={disabled || value.targetCount >= MAX_TARGET}
                        className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-40 text-lg font-bold"
                        aria-label="Augmenter l'objectif"
                    >
                        +
                    </button>
                    <span className="text-sm text-gray-500 ml-2">tampons (= visites)</span>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                    Plage [{MIN_TARGET}, {MAX_TARGET}]. Chaque visite validée ajoute un tampon.
                </p>
            </div>

            {/* Récompense principale */}
            <div>
                <label htmlFor={`${uid}-reward`} className="block text-sm font-bold text-gray-700 mb-2">
                    Récompense finale <span className="text-gray-500 font-normal">— déclenchée à {value.targetCount} tampons</span>
                </label>
                <input
                    id={`${uid}-reward`}
                    type="text"
                    placeholder="Ex: repas offert, menu complet"
                    value={value.rewardLabel}
                    onChange={e => setRewardLabel(e.target.value)}
                    disabled={disabled}
                    className="w-full p-2.5 rounded-xl border border-gray-400 bg-white outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                />
                {value.rewardLabel && (
                    <p className="mt-2 text-xs text-indigo-700 bg-indigo-50 px-3 py-2 rounded-lg">
                        Après <strong>{value.targetCount} visites</strong>, le client reçoit : <strong>{value.rewardLabel}</strong>
                    </p>
                )}
            </div>

            {/* Timeline des tampons + récompenses intermédiaires */}
            <div>
                <p id={`${uid}-tiers`} className="block text-sm font-bold text-gray-700 mb-2">
                    Récompenses intermédiaires <span className="text-gray-500 font-normal">— optionnel</span>
                </p>
                <p id={`${uid}-tiers-help`} className="text-xs text-gray-500 mb-3">
                    Cliquez sur un tampon pour ajouter ou retirer une récompense intermédiaire.
                </p>
                <div className="overflow-x-auto pb-2">
                    <div role="group" aria-labelledby={`${uid}-tiers`} aria-describedby={`${uid}-tiers-help`} className="flex items-center gap-1 min-w-max">
                        {Array.from({ length: value.targetCount }, (_, i) => {
                            const stampNumber = i + 1;
                            const isFinal = stampNumber === value.targetCount;
                            const tier = value.rewardTiers.find(t => t.threshold === stampNumber);
                            const hasReward = isFinal || !!tier;
                            return (
                                <div key={stampNumber} className="flex flex-col items-center gap-1">
                                    <button
                                        type="button"
                                        onClick={() => !isFinal && !disabled && toggleStampReward(stampNumber)}
                                        disabled={disabled || isFinal}
                                        aria-label={isFinal
                                            ? `Tampon ${stampNumber} : récompense finale`
                                            : `Tampon ${stampNumber} : récompense intermédiaire`}
                                        aria-pressed={isFinal ? undefined : !!tier}
                                        className={`w-10 h-10 rounded-full text-xs font-bold border-2 transition-colors ${
                                            isFinal
                                                ? 'bg-amber-100 border-amber-400 text-amber-700 cursor-default'
                                                : tier
                                                    ? 'bg-indigo-100 border-indigo-500 text-indigo-700'
                                                    : 'bg-white border-gray-400 text-gray-600 hover:border-indigo-400'
                                        }`}
                                    >
                                        {isFinal ? <GiftIcon className="w-5 h-5" /> : stampNumber}
                                    </button>
                                    {hasReward && (
                                        <span aria-hidden="true" className="text-xs text-gray-500 font-medium inline-flex items-center gap-1">
                                            {isFinal ? 'final' : <GiftIcon className="w-3 h-3" />}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
                {/* Liste éditable des paliers */}
                {value.rewardTiers.length > 0 && (
                    <ul className="mt-3 space-y-2">
                        {value.rewardTiers.map(tier => (
                            <li key={tier.threshold} className="flex items-center gap-2">
                                <span className="text-xs text-indigo-600 font-bold w-20">Tampon {tier.threshold}</span>
                                <input
                                    type="text"
                                    placeholder="Nom de la récompense"
                                    aria-label={`Récompense au tampon ${tier.threshold}`}
                                    value={tier.label}
                                    onChange={e => updateTierLabel(tier.threshold, e.target.value)}
                                    disabled={disabled}
                                    className="flex-1 p-2 text-sm rounded-xl border border-gray-400 bg-white outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                                />
                                <button
                                    type="button"
                                    onClick={() => toggleStampReward(tier.threshold)}
                                    disabled={disabled}
                                    aria-label={`Supprimer le palier au tampon ${tier.threshold}`}
                                    className="text-brand-600 hover:text-brand-800 disabled:opacity-40 px-2"
                                >
                                    ×
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {errors && errors.length > 0 && (
                <div role="alert" className="p-3 bg-red-50 border border-red-200 rounded-lg space-y-1">
                    {errors.map((err, i) => (
                        <p key={i} className="text-xs text-red-700 dark:text-red-300 inline-flex items-center gap-1.5">
                            <WarningIcon className="w-3.5 h-3.5 flex-shrink-0" /> {err}
                        </p>
                    ))}
                </div>
            )}
        </div>
    );
};

export default StampsConfig;
export type { StampsConfigState };
